// Pieces the three views of an episode (Quick, Script, Timeline) share: the top bar (back, title, assistant, undo and
// redo, export), the view an episode was last left in, and the pages that open over the screen (add/edit pages, the
// export screen, a toast).
import { useMemo, useState } from "react";
import type { TimelineEpisode } from "../../model/timeline";
import { uid } from "../../model/ids";
import { Icon, Toast, TopBar } from "../common";
import { ExportScreen } from "../episode/ExportPage";
import { AssistantButton } from "./Assistant";
import { PAGES, type PageKind } from "./pages";
import { useTimeline } from "./TimelineLoader";

export type View = "quick" | "script" | "timeline";
export type Open = { kind: PageKind; t: number; char?: string; editId?: string; newId?: string; step?: string };

// Each episode remembers its own view in this browser; one never switched opens in Script (episodes made the quick way save "quick" when they are created).
const viewKey = (id: string) => `sketchy.episodeView.${id}`;
export function loadView(id: string): View {
  try { const v = localStorage.getItem(viewKey(id)); return v === "quick" || v === "timeline" ? v : "script"; } catch { return "script"; }
}
export function saveView(id: string, v: View) {
  try { localStorage.setItem(viewKey(id), v); } catch { /* private window: the choice just isn't remembered */ }
}

export const NEEDS_LINE = "Add a line to give it length";

export function EditorBar({ onBack, playable, onExport }: { onBack: () => void; playable: boolean; onExport: () => void }) {
  const { ep, update, undo, redo, canUndo, canRedo } = useTimeline();
  return (
    <TopBar onBack={onBack} right={<>
      <AssistantButton />
      <div className="eb-pill">
        <button className="icon-btn" disabled={!canUndo} onClick={undo} aria-label="Undo"><Icon name="undo" size={21} /></button>
        <button className="icon-btn" disabled={!canRedo} onClick={redo} aria-label="Redo"><Icon name="redo" size={21} /></button>
      </div>
      <button className="icon-btn" disabled={!playable} title={playable ? undefined : NEEDS_LINE} onClick={onExport} aria-label="Export"><Icon name="download" size={22} /></button>
    </>}>
      <input className="title-input" value={ep.title} aria-label="Episode title" onChange={e => { const title = e.target.value; update(x => ({ ...x, title }), { merge: "title" }); }} />
    </TopBar>
  );
}

// The page open over the screen (if any), the export screen and the toast. `stop` halts playback when a page or the export opens.
// Pages stack: a page opened from another (a line opening its picture's Faces) goes back to it, and the first one back to
// the screen. A page that closes itself before opening another replaces itself instead. Work that fails after its
// page has gone (see jobs.ts) shows in the app's toast (App.tsx).
export function useEditorPages(ep: TimelineEpisode, stop: () => void, onSeek: (t: number) => void) {
  const [stack, setStack] = useState<Open[]>([]);
  const open = stack[stack.length - 1] ?? null;
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // A page opened to add something knows the id it will get, so coming back to it (after a page it opened) finds it.
  const withId = (o: Open): Open => (o.editId || o.newId ? o : { ...o, newId: uid(o.kind === "picture" ? "pk" : "new") });
  const openPage = (o: Open) => { stop(); setStack([withId(o)]); };
  const pageKey = useMemo(() => (open ? `${open.kind}-${open.editId ?? open.newId}-${open.t}-${open.char ?? ""}-${open.step ?? ""}` : ""), [open]);
  const Page = open ? PAGES[open.kind] : null;
  const node = (
    <>
      {Page && open && (
        <Page key={pageKey} t={open.t} editId={open.editId} newId={open.newId} step={open.step} char={open.char}
          onClose={() => setStack(s => s.slice(0, -1))} onToast={setToast} onSeek={onSeek}
          onOpen={(kind, o) => setStack(s => [...s, withId({ kind, ...o })])} />
      )}
      {exporting && <ExportScreen ep={ep} onClose={() => setExporting(false)} />}
      {toast && <Toast onClick={() => setToast(null)}>{toast}</Toast>}
    </>
  );
  return { open, openPage, node, exportIt: () => { stop(); setExporting(true); } };
}
